import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaCheckCircle, FaDownload, FaHome, FaImages, FaSpinner, FaShoppingCart } from 'react-icons/fa';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const Success = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [imageUrl, setImageUrl] = useState(null);
  const [downloadUrl, setDownloadUrl] = useState(null);
  const [hairstyleLink, setHairstyleLink] = useState(null);
  const [saved, setSaved] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const verifyPayment = async () => {
      try {
        const params = new URLSearchParams(location.search);
        const sessionId = params.get('session_id');

        if (!sessionId) {
          throw new Error('No session ID found');
        }

        console.log('Verifying payment for session:', sessionId);
        const response = await fetch(`${API_URL}/verify_payment/${sessionId}`, {
          credentials: 'include'
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Payment verification failed');
        }

        const data = await response.json();
        console.log('Payment verification response:', data);

        if (data.success) {
          setImageUrl(`${API_URL}/view-tryon-image/${data.tryon_id}`);
          setDownloadUrl(`${API_URL}/download-tryon-image/${data.tryon_id}`);
          console.log('Hairstyle link from response:', data.hairstyle_link);
          if (data.hairstyle_link) {
            console.log('Setting hairstyle link:', data.hairstyle_link);
            setHairstyleLink(data.hairstyle_link);
          } else {
            console.log('No hairstyle link found in response');
          }
          setSaved(true);
        } else {
          throw new Error(data.message || 'Payment verification failed');
        }
      } catch (error) {
        console.error('Payment verification error:', error);
        setError(error.message || 'Payment verification failed. Please contact support.');
      } finally {
        setLoading(false);
      }
    };

    verifyPayment();
  }, [location.search]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        <div className="bg-white rounded-2xl shadow-xl p-8 space-y-8">
          {/* Header Section */}
          <div className="text-center space-y-4">
            <FaCheckCircle className="mx-auto h-16 w-16 text-teal-500" />
            <h2 className="text-4xl font-extrabold text-gray-900 tracking-tight">
              Payment Successful!
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Your hairstyle has been generated successfully. You can now download your image or view it in your dashboard.
            </p>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <FaSpinner className="animate-spin text-4xl mx-auto mb-4 text-teal-500" />
              <h2 className="text-2xl font-semibold text-gray-900">
                Verifying your payment...
              </h2>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <div className="text-red-600 text-lg mb-6">{error}</div>
              <Link
                to="/try-on"
                className="btn btn-primary"
              >
                Try Again
              </Link>
            </div>
          ) : (
            <div>
              {imageUrl && (
                <div className="mt-8">
                  <div className="relative rounded-lg overflow-hidden shadow-lg mx-auto max-w-md">
                    <img
                      src={imageUrl}
                      alt="Generated Hairstyle"
                      className="w-full h-auto"
                      onError={(e) => {
                        console.error('Error loading image:', e);
                        setError('Failed to load the generated image');
                      }}
                    />
                  </div>

                  <div className="mt-8 flex flex-col sm:flex-row justify-center items-center gap-4">
                    {downloadUrl && (
                      <a
                        href={downloadUrl}
                        download
                        className="btn btn-primary flex items-center gap-2"
                      >
                        <FaDownload /> Download Image
                      </a>
                    )}

                    {hairstyleLink && (
                      <a
                        href={hairstyleLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn bg-gradient-to-r from-teal-500 to-green-500 text-white flex items-center gap-2 transform transition hover:scale-105"
                      >
                        <FaShoppingCart /> Buy This Style
                      </a>
                    )}

                    <Link
                      to="/try-on"
                      className="btn btn-secondary flex items-center gap-2"
                    >
                      <FaImages /> Try Another Style
                    </Link>
                  </div>
                </div>
              )}

              <div className="mt-8 text-center">
                <Link
                  to="/"
                  className="text-teal-600 hover:text-teal-800 flex items-center justify-center gap-2"
                >
                  <FaHome /> Return to Home
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Success;
